import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { formatLocalDate } from '../utils/dateUtils';

const MenuPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contract = location.state?.contract;
  const menuRef = useRef(null);

  if (!contract) {
    navigate('/contracts');
    return null;
  }

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = menuRef.current;
    const opt = {
      margin: 0.5,
      filename: `${contract.event_name}_Menu.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  // Group items by category for display
  const groupItemsByCategory = (items) => {
    const groups = {};
    (items || []).forEach(item => {
      const cat = item.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };

  // Sort categories in logical order
  const categoryOrder = ['Appetizer', 'Bread', 'Salad', 'Soup', 'Side', 'Main', 'Dessert', 'Beverage', 'Other'];
  const sortCategories = (categories) => {
    return Object.keys(categories).sort((a, b) => {
      const aIdx = categoryOrder.findIndex(c => a.toLowerCase().includes(c.toLowerCase()));
      const bIdx = categoryOrder.findIndex(c => b.toLowerCase().includes(c.toLowerCase()));
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      {/* Action Bar */}
      <div className="max-w-3xl mx-auto mb-6 flex justify-between items-center print:hidden">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} className="mr-2" />
          Back
        </Button>
        <div className="flex gap-3">
          <Button onClick={handleDownloadPDF} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white">
            <Download size={20} className="mr-2" />
            Download PDF
          </Button>
          <Button onClick={handlePrint} className="bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
            <Printer size={20} className="mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Menu Card */}
      <div 
        ref={menuRef}
        className="max-w-3xl mx-auto bg-white shadow-2xl"
        style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}
      >
        {/* Header */}
        <div className="bg-gradient-to-b from-[#1e3a5f] to-[#2d5016] text-white text-center py-12 px-8">
          <p className="text-sm tracking-[0.3em] uppercase mb-4 opacity-80">
            {formatLocalDate(contract.event_date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {contract.event_name}
          </h1>
          <div className="w-24 h-0.5 bg-[#c9a227] mx-auto my-4"></div>
          <p className="text-lg opacity-90">{contract.client_name}</p>
        </div>

        {/* Menu Content */}
        <div className="p-8 md:p-12">
          {/* Hors d'Oeuvres */}
          {contract.selected_hors_doeuvres && contract.selected_hors_doeuvres.length > 0 && (
            <div className="mb-10 text-center">
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-1">Hors d'Oeuvres</h2>
              <div className="w-16 h-0.5 bg-[#c9a227] mx-auto mb-4"></div>
              <div className="space-y-2">
                {contract.selected_hors_doeuvres.filter(i => i.sub_category === 'cold').length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Cold Selection</p>
                    {contract.selected_hors_doeuvres.filter(i => i.sub_category === 'cold').map((item, idx) => (
                      <p key={item.id || `cold-${idx}`} className="text-lg text-gray-700">{item.name}</p>
                    ))}
                  </div>
                )}
                {contract.selected_hors_doeuvres.filter(i => i.sub_category === 'hot').length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Hot Selection</p>
                    {contract.selected_hors_doeuvres.filter(i => i.sub_category === 'hot').map((item, idx) => (
                      <p key={item.id || `hot-${idx}`} className="text-lg text-gray-700">{item.name}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Menus */}
          {contract.selected_menus_details && contract.selected_menus_details.map((menu) => {
            if (menu.category === "Hors d'Oeuvres" || menu.category === "Late Night") return null;
            const groupedItems = groupItemsByCategory(menu.items);
            const sortedCategories = sortCategories(groupedItems);

            return (
              <div key={menu.id || menu.name} className="mb-10 text-center">
                <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-1">{menu.name}</h2>
                <div className="w-16 h-0.5 bg-[#c9a227] mx-auto mb-6"></div>
                
                {sortedCategories.map((category) => (
                  <div key={category} className="mb-6">
                    {sortedCategories.length > 1 && (
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">{category}</p>
                    )}
                    <div className="space-y-1">
                      {groupedItems[category].map((item) => (
                        <p key={item.id || item.name} className="text-lg text-gray-700">
                          {item.name}
                          {item.description && (
                            <span className="block text-sm text-gray-500 italic">{item.description}</span>
                          )}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Add-Ons */}
          {contract.selected_addons && contract.selected_addons.length > 0 && (
            <div className="mb-10 text-center">
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-1">Enhancements</h2>
              <div className="w-16 h-0.5 bg-[#c9a227] mx-auto mb-4"></div>
              <div className="space-y-1">
                {contract.selected_addons.map((item) => (
                  <p key={item.id || item.name} className="text-lg text-gray-700">{item.name}</p>
                ))}
              </div>
            </div>
          )}

          {/* Late Night */}
          {contract.selected_late_night && contract.selected_late_night.length > 0 && (
            <div className="mb-10 text-center">
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-1">Late Night</h2>
              <div className="w-16 h-0.5 bg-[#c9a227] mx-auto mb-4"></div>
              <div className="space-y-2">
                {contract.selected_late_night.map((item, idx) => (
                  <div key={item.id || `ln-${idx}`}>
                    <p className="text-lg text-gray-700">{item.name}</p>
                    {item.description && (
                      <p className="text-sm text-gray-500 italic">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bar Service */}
          {contract.bar_type && (
            <div className="mb-10 text-center">
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-1">Bar Service</h2>
              <div className="w-16 h-0.5 bg-[#c9a227] mx-auto mb-4"></div>
              <p className="text-lg text-gray-700 capitalize">{contract.bar_type.replace(/_/g, ' ')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#f5f5dc] text-center py-8 px-8 border-t border-[#c9a227]">
          <p className="text-sm text-gray-600 italic">
            Please inform your server of any dietary restrictions or allergies
          </p>
          <div className="w-12 h-0.5 bg-[#c9a227] mx-auto my-4"></div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">
            Willow Ridge Catering
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #menu-card, #menu-card * {
            visibility: visible;
          }
          #menu-card {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default MenuPreview;
